import axios from "axios";
import { toast } from "react-toastify";
import { API, DEV_USER_ID } from "../constant";
import { Auth } from "aws-amplify";

const getSession = (user, refreshToken) => {
  const promise = new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
  return promise;
};

const requestHandler = async (request) => {
  if (DEV_USER_ID) {
    request.headers["x-user-id"] = DEV_USER_ID;
    return request;
  }
  try {
    const session = await Auth.currentSession();
    var idTokenExpire = session.getIdToken().getExpiration() * 1000;
    var refreshToken = session.getRefreshToken();
    var currentTimeSeconds = new Date().getTime();
    if (idTokenExpire < currentTimeSeconds) {
      const user = await Auth.currentAuthenticatedUser();
      const newSession = await getSession(user, refreshToken);
      request.headers["Authorization"] =
        "Bearer " + newSession.getIdToken().getJwtToken();
    } else
      request.headers["Authorization"] =
        "Bearer " + session.getIdToken().getJwtToken();
  } catch (err) {
    Auth.signOut();
  } finally {
    return request;
  }
};

// Guard so we don't trigger a logout storm if many parallel requests
// all return 401 at once (every SWR hook on the page fires).
let unauthorizedLogoutTriggered = false;

const handleUnauthorized = async (apiMessage) => {
  if (unauthorizedLogoutTriggered) return;
  unauthorizedLogoutTriggered = true;
  // Show the backend's message (e.g. "Email belum terverifikasi.") once
  // so the user understands why they're being signed out — and then
  // actually sign them out, so they can recover without being stuck on
  // a page that re-fires the failing request every render.
  toast.error(
    apiMessage
      ? `${apiMessage} Anda diarahkan ke halaman login.`
      : "Sesi tidak valid. Anda diarahkan ke halaman login."
  );
  try {
    await Auth.signOut();
  } catch (_) {
    /* ignore — fall through to local clear */
  }
  localStorage.clear();
  // Hard navigate so any in-flight requests + cached state are dropped.
  // We use replace() so the broken page is not in the back-button history.
  if (typeof window !== "undefined") {
    window.location.replace("/login");
  }
};

const errorHandler = (error) => {
  // Suppress toast for known self-heal case: first-login GET /company
  // returns 404 until useCompany triggers POST /company.
  const url = error.config && error.config.url;
  const status = error.response && error.response.status;
  const silentNotFound = status === 404 && url === "/company";
  const apiMessage =
    error.response && error.response.data
      ? (error.response.data.error && error.response.data.error.message) ||
        error.response.data.message
      : null;

  // 401 from our backend = the JWT is no longer accepted (expired, email
  // not verified, account disabled, etc.). Don't just spam the toast and
  // let the page re-render in a loop — sign out and bounce to /login.
  if (status === 401 && !DEV_USER_ID) {
    handleUnauthorized(apiMessage);
    return Promise.reject({ ...error });
  }

  if (error.message == "Cannot retrieve a new session. Please authenticate.") {
    localStorage.clear();
  } else if (!silentNotFound) {
    if (apiMessage) {
      toast.error(apiMessage);
    } else if (error.message) {
      toast.error(error.message);
    }
  }
  return Promise.reject({ ...error });
};

const successHandler = (response) => {
  return response;
};
// Init Axios
const axiosInstance = axios.create({
  baseURL: API,
});

// Add interceptors
axiosInstance.interceptors.request.use((request) => requestHandler(request));
axiosInstance.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error)
);

export default axiosInstance;
